import dayjs from "dayjs"

import CopyToClipboard from "#blocks/CopyToClipboard"
import TextOverrun from "#blocks/TextOverrun"
import { type TableData } from "#customTypes/types"
import { camelToWords } from "#utils/textFormatters"

import type { ColumnsType, ColumnType } from "antd/es/table"

/**
 * Structured column definition. Use instead of a plain string key when you
 * need to declare display props (width, align, ellipsis) alongside the field
 * name. More props can be added here as the need arises without requiring a
 * parallel config map.
 */
export interface TableColumnDef {
  key: string
  width?: number | string
  align?: "left" | "center" | "right"
  ellipsis?: boolean
  /** Override the auto-generated camelCase title. */
  title?: string
  /**
   * Hard-truncate the cell string to this many characters, appending "…".
   * Simpler than TextOverrun when the content length is predictable and you
   * don't need a tooltip. Overrides the default TextOverrun render.
   */
  truncate?: number
}

/** A column spec is either a plain field-key string or a structured def. */
export type TableColumnSpec = string | TableColumnDef

/** Extracts the field key from either form of column spec. */
export const getColumnKey = (spec: TableColumnSpec): string =>
  typeof spec === "string" ? spec : spec.key

/**
 * Builds AntD column configs from a plain list of field keys, matching the
 * conventions of the legacy string-column Table: titles from camelCase keys,
 * ellipsis truncation, an id column rendered with copy-to-clipboard.
 *
 * Column specs may be plain strings or `TableColumnDef` objects. Object specs
 * apply width/align/ellipsis before `overrides`, so overrides always win.
 *
 * The generic `sorter` here mirrors useTableSort's comparator (numeric when
 * both values are numbers, otherwise a locale string compare) so AntD's
 * client-side sort of the visible rows matches the hook's full-set ordering.
 */
const genericCompare = (a: unknown, b: unknown): number => {
  if (typeof a === "number" && typeof b === "number") return a - b
  return String(a ?? "").localeCompare(String(b ?? ""))
}

interface BuildTableColumnsOptions<T extends TableData> {
  sortableFields?: readonly string[]
  // Kept for back-compat; TableColumnDef.align is preferred for new columns.
  rightAlignFields?: readonly string[]
  // Resolves the value a column sorts on, for columns whose displayed value is
  // not directly sortable (formatted dates, "8/12" headcounts, string numbers).
  // MUST match the resolver passed to useTableSort so AntD's re-sort of the
  // visible rows agrees with the hook's full-set ordering.
  getSortValue?: (row: T, field: string) => string | number
  // Per-key partial column config merged last (render, width, ellipsis, etc).
  overrides?: Partial<Record<string, Partial<ColumnType<T>>>>
  // Prepend a compact id copy column (DEV-only; gate with the RESOURCES.ID
  // permission in the caller). Skipped if an "id" column is already present.
  showId?: boolean
}

const ID_COLUMN: TableColumnDef = {
  key: "id",
  title: "ID",
  width: 50,
  align: "center",
  ellipsis: false,
}

export const buildTableColumns = <T extends TableData>(
  keys: readonly TableColumnSpec[],
  {
    sortableFields = [],
    rightAlignFields = [],
    getSortValue,
    overrides = {},
    showId = false,
  }: BuildTableColumnsOptions<T> = {}
): ColumnsType<T> => {
  const hasIdColumn = keys.some((spec) => getColumnKey(spec) === "id")
  const finalKeys: readonly TableColumnSpec[] =
    showId && !hasIdColumn ? [ID_COLUMN, ...keys] : keys

  return finalKeys.map((spec) => {
    const key = getColumnKey(spec)
    const def: Partial<Omit<TableColumnDef, "key">> =
      typeof spec === "string" ? {} : spec

    const column: ColumnType<T> = {
      title: def.title ?? camelToWords(key),
      dataIndex: key,
      key,
      ellipsis: true,
    }

    // Apply structured def props. ellipsis from def overrides the default true.
    if (def.width !== undefined) column.width = def.width
    if (def.ellipsis !== undefined) column.ellipsis = def.ellipsis
    if (def.align !== undefined) column.align = def.align

    // rightAlignFields back-compat: def.align wins if both are set.
    if (rightAlignFields.includes(key) && def.align === undefined) {
      column.align = "right"
    }

    if (sortableFields.includes(key)) {
      column.sorter = (a, b) =>
        genericCompare(
          getSortValue ? getSortValue(a, key) : a[key],
          getSortValue ? getSortValue(b, key) : b[key]
        )
      column.showSorterTooltip = { target: "full-header" }
    }

    // For text columns that truncate, use TextOverrun so the cell JS-measures
    // its width and hard-truncates — preventing long content from stretching
    // the column beyond its specified width. Disable AntD's native title
    // tooltip since TextOverrun provides its own on hover.
    if (column.ellipsis) {
      column.ellipsis = { showTitle: false }
      column.render = (value: unknown) => (
        <TextOverrun text={String(value ?? "")} />
      )
    }

    // Hard character-limit truncation — overrides TextOverrun when set.
    if (def.truncate !== undefined) {
      const max = def.truncate
      column.ellipsis = false
      column.render = (value: unknown) => {
        const str = String(value ?? "")
        return str.length > max ? `${str.slice(0, max)}…` : str
      }
    }

    // Special-case renders override the TextOverrun default above.
    if (key === "id") {
      column.render = (value: string) => (
        <CopyToClipboard data={value} compact />
      )
    }

    if (key === "lastUpdatedAt") {
      column.render = (value: string) =>
        value ? dayjs(value).format("M/D h:mm a") : ""
    }

    return { ...column, ...(overrides[key] ?? {}) }
  })
}
